import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Mail, ArrowLeft, RefreshCw, LogOut } from "lucide-react";
import { toast } from "react-toastify";
import registerRequest from "../../../api/Customer/RegisterRequest";
import { setUser, clearUser } from "../../../redux/userSlice";

const VerifyOtp = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [otp, setOtp] = useState(["", "", "", ""]);
  const inputRefs = useRef([]);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { txtconnect_ng_user } = useSelector((state) => state.user);

  const email = location.state?.email || txtconnect_ng_user?.email;
  const user_slug = location.state?.user_slug || txtconnect_ng_user?.user_slug;
  const from = location.state?.from;

  // //console.log("user_slug:", user_slug);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!email && !txtconnect_ng_user && !user_slug) {
      navigate("/register");
      return;
    }
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown, email, txtconnect_ng_user, user_slug, navigate]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 4);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = pastedData.split("").concat(["", "", "", ""]).slice(0, 4);
    setOtp(newOtp);

    const nextIndex = Math.min(pastedData.length, 3);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleVerifyOtp = async () => {
    const otpValue = otp.join("");

    if (otpValue.length !== 4) {
      toast.error("Please enter the complete 4-digit OTP");
      return;
    }

    setVerifyLoading(true);
    try {
      const response = await registerRequest.verifyOtp({
        user_slug: user_slug,
        otp: otpValue,
      });

      // //console.log("API Response:", response); // For debugging

      // Check if response has the expected structure
      if (response && response.data) {
        const responseData = response.data;
        const statusCode = responseData.status_code;
        
        // Check status codes
        if (statusCode === "000") {
          // Success case
          const userData = responseData.data?.[0] || responseData.data;
          const token = userData?.token;
          
          if (token) {
            dispatch(
              setUser({
                userData,
                token,
              })
            );
            
            const successMessage = responseData.message || "Email verified successfully!";
            toast.success(successMessage);
            
            setTimeout(() => {
              navigate("/home");
            }, 1500);
          } else {
            toast.error("Verification response missing token");
            setOtp(["", "", "", ""]);
            inputRefs.current[0]?.focus();
          }
        } else {
          // Error cases based on status codes
          let errorMessage = responseData.reason || 
                            responseData.message || 
                            "Verification failed. Please try again.";
          
          // Add specific handling for known status codes if needed
          switch(statusCode) {
            case "004":
              // OTP not found - message already comes from reason
              break;
            case "999":
              // OTP expired - message already comes from reason
              break;
            default:
              // For other status codes, use the reason from response
              break;
          }
          
          toast.error(errorMessage);
          setOtp(["", "", "", ""]);
          inputRefs.current[0]?.focus();
        }
      } else {
        // If response doesn't have the expected structure
        toast.error("Invalid response from server");
        setOtp(["", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      // Handle API errors
      console.error("Verification error:", error);
      
      let errorMessage = "Verification failed. Please try again.";
      
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        // Check both root level and nested data structure
        if (errorData.data) {
          errorMessage = errorData.data.reason || 
                        errorData.data.message || 
                        errorMessage;
        } else {
          errorMessage = errorData.reason || 
                        errorData.message || 
                        error.message || 
                        errorMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      setOtp(["", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
      const response = await registerRequest.resendVerification({
        emailorPhone: email,
      });

      // //console.log("Resend Response:", response); // For debugging

      if (response && response.data) {
        const responseData = response.data;
        const statusCode = responseData.data.status_code;

        // //console.log("Resend Status Code:", statusCode); // For debugging
        
        // Check status code for success
        if (statusCode === "000") {
          toast.success(
            responseData.message || "Verification code sent successfully! Kindly check your email or spam messages"
          );
          setCountdown(60);
          setOtp(["", "", "", ""]);
          inputRefs.current[0]?.focus();
        } else {
          // Show the reason from response for errors
          const errorMessage = responseData.reason || 
                              responseData.message ||
                              "Failed to resend verification code. Please try again.";
          toast.error(errorMessage);
        }
      } else {
        toast.error("Failed to resend verification code. Please try again.");
      }
    } catch (error) {
      // Handle API errors for resend
      let errorMessage = "Failed to resend verification code. Please try again.";
      
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.data) {
          errorMessage = errorData.data.reason || 
                        errorData.data.message || 
                        errorMessage;
        } else {
          errorMessage = errorData.reason || 
                        errorData.message || 
                        errorMessage;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  const handleGoToLogin = () => {
    navigate("/login");
  };

  const handleLogout = () => {
    dispatch(clearUser());
    toast.success("Logged out successfully");
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-teal-50 flex items-center justify-center p-4">
      <div
        className={`w-full max-w-md transition-all duration-500 ease-in-out ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header with Logout Button */}
          <div className="flex justify-between items-start mb-6">
            <div className="flex-1">
              <div className="mx-auto w-16 h-16 bg-gradient-to-r from-red-600 to-teal-600 rounded-full flex items-center justify-center mb-4">
                <Mail className="w-8 h-8 text-white" />
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-1 text-gray-500 hover:text-red-600 transition-colors duration-300 text-sm"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Verify Your Account
            </h1>
            <p className="text-gray-600 text-sm">
              Enter the 4-digit verification code sent to your email
            </p>
            {email && (
              <p className="text-sm text-gray-500 mt-2">
                Sent to: <span className="font-medium">{email}</span>
              </p>
            )}
          </div>

          {/* OTP Input */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-4 text-center">
              Verification Code
            </label>
            <div className="flex justify-center space-x-3 mb-6">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className="w-12 h-12 text-center text-lg font-semibold border-2 border-gray-300 rounded-lg focus:border-red-500 focus:ring-red-500 outline-none transition-all duration-300"
                  placeholder="0"
                />
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-4">
            <button
              onClick={handleVerifyOtp}
              disabled={verifyLoading || otp.join("").length !== 4}
              className="w-full py-3 px-4 bg-gradient-to-r from-red-600 to-teal-600 text-white rounded-lg font-medium hover:from-red-700 hover:to-teal-700 transition-all duration-300 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {verifyLoading ? "Verifying..." : "Verify Account"}
            </button>

            <button
              onClick={handleResendVerification}
              disabled={resendLoading || countdown > 0}
              className="w-full py-3 px-4 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {resendLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Sending...</span>
                </>
              ) : countdown > 0 ? (
                <span>Resend in {countdown}s</span>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  <span>Resend Verification Code</span>
                </>
              )}
            </button>

            <button
              onClick={handleGoToLogin}
              className="w-full py-3 px-4 text-red-600 hover:text-red-700 font-medium transition-all duration-300"
            >
              Already verified? Go to Login
            </button>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm">
              {/* <Link
                to={from === "register" ? "/register" : "/login"}
                className="flex items-center space-x-1 text-gray-600 hover:text-gray-800 transition-colors duration-300"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>
                  Back to {from === "register" ? "Register" : "Login"}
                </span>
              </Link> */}

            
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Didn't receive the code? Check your spam folder or{" "}
            <button
              onClick={handleResendVerification}
              disabled={countdown > 0}
              className="text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
            >
              request a new one
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyOtp;